import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { ProfileRepository } from '../repositories/profile.repository';
import { NotificationService } from '../../shared/services/notification.service';
import { ProfileSelection, Profile, ImpersonationStart, ProfileSearchCriteria, ProfilesListResponse, CreateUserRequest, CreateUserResponse } from '../interfaces/api.interface';

/**
 * Profile Service
 * Business logic layer for profile operations
 * Handles error notifications and delegates HTTP calls to ProfileRepository
 */
@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly profileRepository = inject(ProfileRepository);
  private readonly notificationService = inject(NotificationService);

  // State management for profiles list
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _profiles = signal<Profile[]>([]);
  private readonly _totalSize = signal<number>(0);

  // Public computed properties
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());
  readonly profiles = computed(() => this._profiles());
  readonly totalSize = computed(() => this._totalSize());

  /**
   * Search profiles with criteria (admin-only)
   */
  searchProfiles(criteria: ProfileSearchCriteria = {}): Observable<Profile[]> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.profileRepository.getProfiles(criteria).pipe(
      map(response => {
        this._profiles.set(response.records);
        this._totalSize.set(response.totalSize);
        return response.records;
      }),
      catchError(error => {
        if (error.status === 403) {
          this._error.set('Acesso negado. Apenas administradores podem aceder a esta funcionalidade.');
          this.notificationService.showError('Apenas utilizadores administradores podem aceder a esta funcionalidade');
        } else {
          this._error.set('Erro ao carregar perfis. Por favor, tente novamente.');
          this.notificationService.showError('Erro ao carregar perfis. Por favor, tente novamente.');
        }
        this._profiles.set([]);
        throw error;
      }),
      finalize(() => this._isLoading.set(false))
    );
  }

  /**
   * Clear current profiles data
   */
  clearProfiles(): void {
    this._profiles.set([]);
    this._totalSize.set(0);
    this._error.set(null);
  }

  /**
   * Get profiles for selection with error handling
   * @param name Optional name filter (case-insensitive partial match)
   * @param skip Number of records to skip (default: 0)
   * @param take Number of records to return (default: 100)
   */
  getProfilesForSelection(
    name?: string, 
    skip: number = 0, 
    take: number = 100
  ): Observable<ProfileSelection[]> {
    return this.profileRepository.getProfilesForSelection(name, skip, take).pipe(
      catchError(error => {
        if (error.status === 403) {
          this.notificationService.showError('Apenas utilizadores administradores podem aceder a esta funcionalidade');
        } else {
          this.notificationService.showError('Erro ao carregar perfis. Por favor, tente novamente.');
        }
        throw error;
      })
    );
  }

  /** Start "Ver como" on a profile, with error handling. */
  startImpersonation(profileId: string): Observable<ImpersonationStart> {
    return this.profileRepository.startImpersonation(profileId).pipe(
      catchError(error => {
        if (error.status === 403) {
          this.notificationService.showError('Apenas administradores podem usar "Ver como".');
        } else if (error.status === 404) {
          this.notificationService.showError('Farmácia ou conta não encontrada.');
        } else if (error.status === 400) {
          this.notificationService.showError('Não foi possível abrir "Ver como". Tente novamente.');
        } else {
          this.notificationService.showError('Erro do servidor. Por favor, tente novamente mais tarde.');
        }
        throw error;
      })
    );
  }

  endImpersonation(): Observable<void> {
    return this.profileRepository.endImpersonation();
  }

  /**
   * Create a new Salesforce User and linked Account (admin-only)
   */
  createUser(data: CreateUserRequest): Observable<CreateUserResponse> {
    return this.profileRepository.createUser(data).pipe(
      catchError(error => {
        const message = error.error?.error || 'Erro ao criar utilizador. Por favor, tente novamente.';
        this.notificationService.showError(message);
        throw error;
      })
    );
  }
}
